import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { productAPI, categoryAPI, sliderAPI, testimonialAPI, promoBannerAPI, homepageCategoryAPI, editorialBannerAPI, IMAGE_BASE } from '../../services/api';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useCart } from '../../context/CartContext';
import useScrollReveal from '../../hooks/useScrollReveal';
import {
    ChevronLeft, ChevronRight, ArrowRight,
    Star, Eye, Truck, PackageCheck, ShoppingBag, Sparkles, Tag, Clock, Heart
} from 'lucide-react';
import { useFavorites } from '../../context/FavoritesContext';
import useSeo from '../../hooks/useSeo';

function TestimonialsSlider({ testimonials }) {
    const trackRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [visibleCount, setVisibleCount] = useState(3);
    const [isMobile, setIsMobile] = useState(false);
    const autoPlayRef = useRef(null);

    useEffect(() => {
        const updateVisible = () => {
            const w = window.innerWidth;
            setIsMobile(w <= 768);
            if (w <= 1024) setVisibleCount(2);
            else setVisibleCount(3);
        };
        updateVisible();
        window.addEventListener('resize', updateVisible);
        return () => window.removeEventListener('resize', updateVisible);
    }, []);

    const maxIndex = Math.max(0, testimonials.length - visibleCount);

    const goTo = useCallback((idx) => {
        setCurrentIndex(Math.max(0, Math.min(idx, maxIndex)));
    }, [maxIndex]);

    useEffect(() => {
        if (isMobile || testimonials.length === 0) return;
        autoPlayRef.current = setInterval(() => {
            setCurrentIndex(prev => prev >= maxIndex ? 0 : prev + 1);
        }, 4000);
        return () => clearInterval(autoPlayRef.current);
    }, [maxIndex, isMobile, testimonials.length]);

    const pauseAuto = () => clearInterval(autoPlayRef.current);
    const resumeAuto = () => {
        if (isMobile) return;
        clearInterval(autoPlayRef.current);
        autoPlayRef.current = setInterval(() => {
            setCurrentIndex(prev => prev >= maxIndex ? 0 : prev + 1);
        }, 4000);
    };

    if (testimonials.length === 0) return null;

    if (isMobile) {
        return (
            <section className="testimonials-section">
                <div className="np-section-head reveal">
                    <span className="np-section-overtitle"><Star size={12} /> Müşteri Yorumları</span>
                    <h2>Müşterilerimiz Ne Diyor?</h2>
                </div>
                <div className="testimonials-grid-mobile reveal">
                    {testimonials.map((t, i) => (
                        <div key={t.id || i} className="testimonial-card-mobile">
                            <div className="testimonial-mobile-left">
                                <div className="testimonial-avatar">{t.initials}</div>
                            </div>
                            <div className="testimonial-mobile-right">
                                <div className="testimonial-mobile-top">
                                    <span className="testimonial-mobile-name">{t.name}</span>
                                    <div className="testimonial-stars">
                                        {[...Array(5)].map((_, si) => (
                                            <Star key={si} size={10} fill={si < t.rating ? '#f9ca24' : 'none'} />
                                        ))}
                                    </div>
                                </div>
                                <p className="testimonial-mobile-text">{t.text}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    return (
        <section className="testimonials-section" onMouseEnter={pauseAuto} onMouseLeave={resumeAuto}>
            <div className="np-section-head reveal">
                <span className="np-section-overtitle"><Star size={12} /> Müşteri Yorumları</span>
                <h2>Müşterilerimiz Ne Diyor?</h2>
                <p>Binlerce mutlu müşterimizden bazıları</p>
            </div>

            <div className="testimonials-slider reveal">
                <button className="testimonial-arrow testimonial-arrow-left" onClick={() => goTo(currentIndex - 1)} disabled={currentIndex === 0}>
                    <ChevronLeft size={22} />
                </button>

                <div className="testimonials-track-wrapper">
                    <div
                        ref={trackRef}
                        className="testimonials-track"
                        style={{ transform: `translateX(-${currentIndex * (100 / visibleCount)}%)` }}
                    >
                        {testimonials.map((t, i) => (
                            <div key={t.id || i} className="testimonial-slide" style={{ minWidth: `${100 / visibleCount}%` }}>
                                <div className="testimonial-card">
                                    <div className="testimonial-header">
                                        <div className="testimonial-avatar">{t.initials}</div>
                                        <div className="testimonial-info">
                                            <h4>{t.name}</h4>
                                            <span className="testimonial-date">{t.date}</span>
                                        </div>
                                    </div>
                                    <div className="testimonial-stars">
                                        {[...Array(5)].map((_, si) => (
                                            <Star key={si} size={16} fill={si < t.rating ? '#f9ca24' : 'none'} />
                                        ))}
                                    </div>
                                    <p className="testimonial-text">{t.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <button className="testimonial-arrow testimonial-arrow-right" onClick={() => goTo(currentIndex + 1)} disabled={currentIndex >= maxIndex}>
                    <ChevronRight size={22} />
                </button>
            </div>

            <div className="testimonial-dots">
                {Array.from({ length: maxIndex + 1 }, (_, i) => (
                    <button key={i} className={`testimonial-dot ${i === currentIndex ? 'active' : ''}`} onClick={() => goTo(i)} />
                ))}
            </div>
        </section>
    );
}

// Product card component - imported from shared component
import ProductCard from '../../components/ProductCard';

export default function Home() {
    useSeo({
        title: 'Noora Privé | Butik Kadın Giyim & Aksesuar Online Mağaza',
        description: 'Noora Privé online butik kadın giyim mağazası. Elbise, bluz, dış giyim, takım, aksesuar ve şal koleksiyonları. Şık tasarım, kaliteli kumaş, hızlı kargo, güvenli ödeme. Türkiye geneli ücretsiz kargo.',
        keywords: 'Noora Privé, noora prive, nooraprive, butik kadın giyim, online butik, kadın giyim mağazası, kadın elbise, şık bluz, dış giyim, kadın takım elbise, aksesuar, şal, eşarp, online kadın giyim, butik moda, özel tasarım kıyafet, premium kadın giyim, İzmir butik, online alışveriş, ücretsiz kargo',
        canonical: 'https://nooraprive.com/'
    });
    const [featured, setFeatured] = useState([]);
    const [newArrivals, setNewArrivals] = useState([]);
    const [discounted, setDiscounted] = useState([]);
    const [categories, setCategories] = useState([]);
    const [heroSlides, setHeroSlides] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [banners, setBanners] = useState([]);
    const [homepageCategories, setHomepageCategories] = useState([]);
    const [editorial, setEditorial] = useState(null);
    const [loading, setLoading] = useState(true);
    const [addingId, setAddingId] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const navigate = useNavigate();
    const { addItem } = useCart();
    const { isFavorited, toggleFavorite } = useFavorites();
    const revealRef = useScrollReveal();
    const { settings } = useSiteSettings();

    const nextSlide = useCallback(() => {
        setCurrentSlide(prev => heroSlides.length > 0 ? (prev + 1) % heroSlides.length : 0);
    }, [heroSlides.length]);

    useEffect(() => {
        if (heroSlides.length === 0) return;
        const timer = setInterval(nextSlide, 6000);
        return () => clearInterval(timer);
    }, [nextSlide, heroSlides.length]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [prodRes, newRes, dealsRes, catRes, sliderRes, testimonialRes, bannerRes, hcRes, editorialRes] = await Promise.all([
                    productAPI.getAll({ featured: 'true', active: 'true', limit: 8 }),
                    productAPI.getAll({ active: 'true', sort: 'newest', limit: 6 }),
                    productAPI.getAll({ deals: 'true', active: 'true', limit: 4 }),
                    categoryAPI.getAll({ active: 'true' }),
                    sliderAPI.getAll({ active: 'true' }),
                    testimonialAPI.getAll({ active: 'true' }),
                    promoBannerAPI.getAll({ active: 'true' }),
                    homepageCategoryAPI.getAll(),
                    editorialBannerAPI.get()
                ]);
                const allFeatured = prodRes.data.products || [];
                const allNew = newRes.data.products || [];
                const dealProducts = dealsRes.data.products || [];
                setFeatured(allFeatured);
                setNewArrivals(allNew.slice(0, 6));
                setDiscounted(dealProducts);
                setCategories((catRes.data || []).filter(c => !c.parentId));
                setHeroSlides(sliderRes.data || []);
                setTestimonials(testimonialRes.data || []);
                setBanners(bannerRes.data || []);
                setHomepageCategories(hcRes.data || []);
                if (editorialRes.data && editorialRes.data.isActive) setEditorial(editorialRes.data);
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        fetchData();
    }, []);

    const formatPrice = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);

    const handleAddToCart = async (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        if (product.hasVariants || product.sizes?.length > 0) {
            navigate(`/urun/${product.slug}`);
            return;
        }
        setAddingId(product.id);
        await addItem(product.id);
        setAddingId(null);
    };

    return (
        <div ref={revealRef}>
            {/* Hero Slider */}
            {heroSlides.length > 0 && (
                <section className="hero-slider">
                    {heroSlides.map((slide, index) => {
                        const imgUrl = slide.image ? (slide.image.startsWith('http') ? slide.image : IMAGE_BASE + slide.image) : null;
                        return (
                            <div
                                key={slide.id || index}
                                className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
                            >
                                {imgUrl && <img src={imgUrl} alt={slide.title} className="hero-slide-img" />}
                                <div className="hero-slide-overlay" />
                                <div className="hero-content">
                                    {slide.badge && (
                                        <div className="hero-badge">
                                            {slide.badge}
                                        </div>
                                    )}
                                    <h1>{slide.title}</h1>
                                    {slide.description && <p className="hero-desc">{slide.description}</p>}
                                    <Link to={slide.buttonLink || '/urunler'} className="hero-explore-btn">
                                        <span>{slide.buttonText || 'Keşfet'}</span>
                                        <ArrowRight size={15} />
                                    </Link>
                                </div>
                            </div>
                        );
                    })}

                    <div className="hero-progress">
                        {heroSlides.map((_, index) => (
                            <button
                                key={index}
                                className={`hero-progress-item ${index === currentSlide ? 'active' : ''}`}
                                onClick={() => setCurrentSlide(index)}
                            >
                                <div className="hero-progress-bar" />
                            </button>
                        ))}
                    </div>

                    <div className="hero-counter">
                        <span className="hero-counter-current">{String(currentSlide + 1).padStart(2, '0')}</span>
                        <span className="hero-counter-divider">/</span>
                        <span className="hero-counter-total">{String(heroSlides.length).padStart(2, '0')}</span>
                    </div>

                    <button className="hero-arrow hero-arrow-left" onClick={() => setCurrentSlide(prev => prev === 0 ? heroSlides.length - 1 : prev - 1)}>
                        <ChevronLeft size={24} />
                    </button>
                    <button className="hero-arrow hero-arrow-right" onClick={() => setCurrentSlide(prev => (prev + 1) % heroSlides.length)}>
                        <ChevronRight size={24} />
                    </button>
                </section>
            )}

            {/* Marquee */}
            <div className="marquee-strip">
                <div className="marquee-track">
                    {[...Array(2)].map((_, i) => (
                        <div className="marquee-content" key={i} aria-hidden={i > 0}>
                            <span>YENİ SEZON</span><span className="marquee-dot" />
                            <span>ÜCRETSİZ KARGO</span><span className="marquee-dot" />
                            <span>ÖZEL KOLEKSİYON</span><span className="marquee-dot" />
                            <span>HIZLI TESLİMAT</span><span className="marquee-dot" />
                            <span>GÜVENLİ ÖDEME</span><span className="marquee-dot" />
                            <span>YENİ SEZON</span><span className="marquee-dot" />
                            <span>ÜCRETSİZ KARGO</span><span className="marquee-dot" />
                            <span>ÖZEL KOLEKSİYON</span><span className="marquee-dot" />
                            <span>HIZLI TESLİMAT</span><span className="marquee-dot" />
                            <span>GÜVENLİ ÖDEME</span><span className="marquee-dot" />
                        </div>
                    ))}
                </div>
            </div>

            {/* Slim Promo Banner */}
            {banners.length > 0 && (
                <Link to={banners[0].buttonLink || '/urunler'} className="slim-promo-banner">
                    <div className="slim-promo-banner-text">
                        <span className="slim-promo-banner-title">{banners[0].title}</span>
                        {banners[0].description && <span className="slim-promo-banner-desc">{banners[0].description}</span>}
                    </div>
                    {banners[0].buttonText && (
                        <span className="slim-promo-banner-cta">
                            {banners[0].buttonText}
                            <ArrowRight size={13} />
                        </span>
                    )}
                </Link>
            )}

            {/* Category Cards */}
            {homepageCategories.length > 0 && (
                <section className="cat-highlight">
                    <div className="cat-highlight-grid reveal">
                        {homepageCategories.map(hc => {
                            const catId = hc.categoryId;
                            const imgSrc = hc.image ? (hc.image.startsWith('http') ? hc.image : IMAGE_BASE + hc.image) : null;
                            return (
                                <Link key={hc.id} to={catId ? `/urunler?category=${catId}` : '/urunler'} className="cat-highlight-card">
                                    {imgSrc && <img src={imgSrc} alt={hc.title} className="cat-highlight-img" />}
                                    <div className="cat-highlight-overlay" />
                                    <div className="cat-highlight-text">
                                        <h3>{hc.title}</h3>
                                        <span className="cat-highlight-cta">Keşfet</span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Sizin İçin Seçtiklerimiz (Featured) */}
            <section className="np-home-section">
                <div className="np-section-head reveal">
                    <span className="np-section-overtitle"><Sparkles size={12} /> Sizin İçin Seçtiklerimiz</span>
                    <h2>Öne Çıkan Koleksiyon</h2>
                    <p>Özenle seçilmiş, her mevsim vazgeçilmez parçalar</p>
                </div>

                {loading ? (
                    <div className="loading"><div className="spinner"></div></div>
                ) : (
                    <>
                        <div className="products-grid reveal reveal-stagger">
                            {featured.map(product => (
                                <ProductCard
                                    key={product.id}
                                    product={product}
                                    formatPrice={formatPrice}
                                    addingId={addingId}
                                    handleAddToCart={handleAddToCart}
                                    isFavorited={isFavorited}
                                    toggleFavorite={toggleFavorite}
                                    showDescription
                                    showShippingBadges
                                    discountLabel
                                />
                            ))}
                        </div>
                        <div className="np-section-cta reveal">
                            <Link to="/urunler" className="np-view-all-btn">
                                <span>Tüm Koleksiyonu Gör</span>
                                <ArrowRight size={15} />
                            </Link>
                        </div>
                    </>
                )}
            </section>

            {/* Editorial Banner */}
            {editorial && (
                <section className="np-editorial reveal">
                    <div className="np-editorial-inner">
                        <div className="np-editorial-img">
                            <img src={editorial.image ? (editorial.image.startsWith('http') ? editorial.image : IMAGE_BASE + editorial.image) : ''} alt={editorial.overtitle} />
                        </div>
                        <div className="np-editorial-content">
                            <span className="np-section-overtitle">{editorial.overtitle}</span>
                            <h2 dangerouslySetInnerHTML={{ __html: editorial.title }} />
                            <p>{editorial.description}</p>
                            <Link to={editorial.buttonLink || '/urunler'} className="np-editorial-btn">
                                <span>{editorial.buttonText || 'Keşfet'}</span>
                                <ArrowRight size={15} />
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* Haftanın Fırsatları */}
            {discounted.length > 0 && (
                <section className="np-home-section np-home-section-alt">
                    <div className="np-section-head reveal">
                        <span className="np-section-overtitle"><Tag size={12} /> Haftanın Fırsatları</span>
                        <h2>Kaçırılmayacak İndirimler</h2>
                        <p>Sınırlı süre ile özel fiyatlar</p>
                    </div>
                    <div className="products-grid np-deals-grid reveal reveal-stagger">
                        {discounted.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                formatPrice={formatPrice}
                                addingId={addingId}
                                handleAddToCart={handleAddToCart}
                                isFavorited={isFavorited}
                                toggleFavorite={toggleFavorite}
                                showDescription
                                showShippingBadges
                                discountLabel
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* Yeni Gelenler */}
            {newArrivals.length > 0 && (
                <section className="np-home-section">
                    <div className="np-section-head reveal">
                        <span className="np-section-overtitle"><Clock size={12} /> Yeni Gelenler</span>
                        <h2>En Son Eklenenler</h2>
                        <p>Koleksiyona yeni eklenen parçalar</p>
                    </div>
                    <div className="products-grid reveal reveal-stagger">
                        {newArrivals.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                formatPrice={formatPrice}
                                addingId={addingId}
                                handleAddToCart={handleAddToCart}
                                isFavorited={isFavorited}
                                toggleFavorite={toggleFavorite}
                                showDescription
                                showShippingBadges
                                discountLabel
                            />
                        ))}
                    </div>
                    <div className="np-section-cta reveal">
                        <Link to="/urunler" className="np-view-all-btn">
                            <span>Tümünü Gör</span>
                            <ArrowRight size={15} />
                        </Link>
                    </div>
                </section>
            )}

            {/* Trust Strip */}
            <section className="np-trust reveal">
                <div className="np-trust-inner">
                    <div className="np-trust-item">
                        <Truck size={22} />
                        <div>
                            <strong>Ücretsiz Kargo</strong>
                            <span>2.000₺ ve üzeri siparişlerde</span>
                        </div>
                    </div>
                    <div className="np-trust-item">
                        <PackageCheck size={22} />
                        <div>
                            <strong>Kolay İade</strong>
                            <span>14 gün içinde ücretsiz</span>
                        </div>
                    </div>
                    <div className="np-trust-item">
                        <ShoppingBag size={22} />
                        <div>
                            <strong>Güvenli Ödeme</strong>
                            <span>iyzico güvencesiyle</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
